/**
 * Data for a promotion page's cross-link rails (web plan W0.9): "More from
 * {Brand}" and "Similar {Category} deals", up to RAIL_SIZE each, from the
 * public `publishedPromotions` feed (two small requests, each only once the
 * promotion has loaded and only when it has a brand / a category).
 *
 * The rails leave out the page's own promotion, and the similar rail leaves
 * out the brand's (the brand rail has them), so no card appears twice. A rail
 * that failed to load is simply empty: it is an extra, never an error state.
 */
import { useFilteredPromotions } from '@/hooks/useAppData';
import {
  RAIL_FETCH_LIMIT,
  brandRailWhere,
  promotionRailCategory,
  railItems,
  similarRailWhere,
  type RailCategory,
  type RailCategorySource,
} from '@/seo/promotionRails';
import type { Promotion } from '@/types';

export interface PromotionRails {
  /** The brand's other listed promotions (upcoming and live; ended ones are never listed). */
  brand: Promotion[];
  /**
   * The brand rail has a successful answer: only then may an empty rail say
   * "no other offers" (never while loading, never after a failed request).
   */
  brandSettled: boolean;
  /** The similar rail's category, or null when the promotion has none worth naming (no rail). */
  category: RailCategory | null;
  /** Listed promotions of other brands in `category`. */
  similar: Promotion[];
}

/** The fields the rails read from the page's promotion. */
export interface PromotionRailsSource {
  id: string;
  brand?: { id: string } | null;
  categories?: readonly (RailCategorySource | null | undefined)[] | null;
}

export function usePromotionRails(promo: PromotionRailsSource | null): PromotionRails {
  const brandId = promo?.brand?.id ?? null;
  const category = promotionRailCategory(promo?.categories);

  const byBrand = useFilteredPromotions({
    page: 1,
    limit: RAIL_FETCH_LIMIT,
    where: brandId ? brandRailWhere(brandId) : undefined,
    skip: !brandId,
  });
  const byCategory = useFilteredPromotions({
    page: 1,
    limit: RAIL_FETCH_LIMIT,
    where: category ? similarRailWhere(category, brandId) : undefined,
    skip: !category,
  });

  const self = promo ? [promo.id] : [];
  const brand = brandId ? railItems(byBrand.items, self) : [];
  const similar = category ? railItems(byCategory.items, [...self, ...brand.map((p) => p.id)]) : [];
  return {
    brand,
    brandSettled: Boolean(brandId) && !byBrand.loading && !byBrand.error,
    category,
    similar,
  };
}

export default usePromotionRails;
